//NPM LIBRARIES
import React, {Component} from 'react';
import PropTypes from "prop-types";
//COMPONENTS AND UTILITIES
import {AUformGroup, AUlabel} from "@gov.au/form";
import {validateAnswerHasValue} from "../Utilities";

class FilePicker extends Component {
    constructor(props) {
        super(props);

        let answer = {
            value: '',
            fileDetails: []
        };

        let errorMessage = '';

        if (props.existingAnswer) {
            answer = props.existingAnswer
            if (props.mandatory) {
                errorMessage = this.validateUploadedFiles(answer.fileDetails)
            }
        } else {
            errorMessage = validateAnswerHasValue(this.props.type, answer, this.props.mandatory);
        }

        this.state = {
            answer: answer,
            errorMessage: errorMessage
        }
    }

    /** Change event is triggered when user uploads the documents. This function adds the uploaded files to state and props
     * @param event
     */
    changeEvent = (event) => {
        let uploadedFiles = event.target.files,
            answer = {
                value: '',
                fileDetails: []
            },
            errorMessage = '';

        if (uploadedFiles) {
            let fileDetails = [];
            for (const file of uploadedFiles) {
                if (!this.state.answer.fileDetails.filter((item) => item.name === file.name).length) {
                    let fileDetail = {};
                    fileDetail.file = file;
                    fileDetail.name = file.name
                    fileDetail.blobURL = URL.createObjectURL(file);
                    fileDetail.UUID = Math.floor(Math.random() * 100000000);
                    fileDetail.isValid = true;
                    fileDetails.push(fileDetail);
                }
            }
            if (fileDetails.length > 0) {
                let finalUploadedList = this.state.answer.fileDetails.concat(fileDetails);
                errorMessage = this.validateUploadedFiles(finalUploadedList);
                let answerValue = errorMessage ? "" : this.getFileNamesAsText(finalUploadedList);
                answer = {
                    value: answerValue,
                    fileDetails: finalUploadedList
                }
                this.setState({answer: answer, errorMessage: errorMessage});
                this.updateProps(errorMessage, answer);
            }
        }
    }

    /** This method validates the files uploaded and returns the appropriate error message if validation fails
     *
     * @param uploadedFiles - FileList Object (List containing the uploaded files)
     * @returns {string} - Empty string or the respective error message
     */
    validateUploadedFiles = (uploadedFiles) => {
        var errorMessage = '';

        var rg1 = /^[^\\/:*?"<>|]+$/, // forbidden characters \ / : * ? " < > |
            rg2 = /^\./, // cannot start with dot (.)
            rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i, // forbidden file names
            rg4 = /^[\w\s.-]+$/;

        //Validating file names
        for (const uploadedFile of uploadedFiles) {
            if (!(rg1.test(uploadedFile.name) && !rg2.test(uploadedFile.name) && !rg3.test(uploadedFile.name) && rg4.test(uploadedFile.name))) {
                uploadedFile.isValid = false;
                errorMessage = this.props.errorMessages.invalidFileName;
            } else {
                uploadedFile.isValid = true;
            }
        }
        //Validating file size
        if (!errorMessage) {
            for (const uploadedFile of uploadedFiles) {
                let fileSize = uploadedFile.file.size / 1024;
                if (fileSize > this.props.maxFileSize) {
                    uploadedFile.isValid = false;
                    errorMessage = this.props.errorMessages.maxSizeReached;
                } else {
                    uploadedFile.isValid = true;
                }
            }
        }
        //Validating file type
        if (!errorMessage) {
            for (const uploadedFile of uploadedFiles) {
                const fileNameParts = uploadedFile.file.name.split('.');
                const fileExtension = fileNameParts[fileNameParts.length - 1].toLowerCase();
                const acceptedFileTypes = this.props.acceptedFiles.toLowerCase().split(',');
                
                if (acceptedFileTypes.indexOf('.' + fileExtension) === -1) {
                    uploadedFile.isValid = false;
                    errorMessage = this.props.errorMessages.invalidFileType;
                }
                else {
                    uploadedFile.isValid = true;
                }
            }
        }
        //validating no of files
        if (!errorMessage && (this.props.maxNoOfFilesAllowed < uploadedFiles.length)) {
            errorMessage = this.props.errorMessages.maxNoOfFilesReached;
        }

        return errorMessage;
    }

    /** This method removes the document from the state and props
     *
     * @param fileId - UUID of the file that needs to be removed
     */
    removeDocument = (fileId) => {
        var finalList = this.state.answer.fileDetails.filter((item) => item.UUID !== fileId),
            answer,
            errorMessage = '';

        answer = {
            value: finalList.length ? this.getFileNamesAsText(finalList) : "",
            fileDetails: finalList
        }
        errorMessage = validateAnswerHasValue(this.props.type, answer, this.props.mandatory);

        if (!errorMessage) {
            errorMessage = this.validateUploadedFiles(finalList);
        }

        this.setState({answer: answer, errorMessage: errorMessage});
        this.updateProps(errorMessage, answer);
    }

    getFileNamesAsText = (uploadedFiles) => {
        let fileNames = [];

        uploadedFiles.forEach( file => {
            fileNames.push(file.name);
        })

        return fileNames.join(", ");
    }

    updateProps = (errorMessage, answer) => {
        let questionObject = {
            stepid: this.props.stepid,
            id: this.props.id,
            errorMessage: errorMessage,
            mandatory: this.props.mandatory,
            label: this.props.label,
            answer: answer,
            type: this.props.type
        }
        this.props.returnQuestionData(questionObject);
    }

    addFilesKeyPressEvent = (event, buttonId) => {
        if (event.which === 13 || event.keyCode === 13) {
            document.getElementById(buttonId).click();
        }
    }

    removeFileKeyPressEvent = (event, fileId) => {
        if (event.which === 13 || event.keyCode === 13) {
            this.removeDocument(fileId)
        }
    }

    /** This method returns the file size in MB
     *
     * @param file - File object
     * @returns {string} - File size in MB
     */
    getFileSize = (file) => {
        let fileSize = '';
        if (file) {
            fileSize = (file.size / (1024 * 1024)).toFixed(2);
        }
        return fileSize;
    }

    render() {
        var allowMultipleUploads = this.props.maxNoOfFilesAllowed > 1,
            disableButton = this.props.maxNoOfFilesAllowed <= this.state.answer.fileDetails.length,
            buttonUniqueId = "file-picker-" + this.props.id + Math.floor(Math.random() * 100000000);

        return (
            <AUformGroup
                className="file-picker"
                status={this.props.getStatus(this.state.errorMessage)}>
                <div className="file-picker-button">
                    <h3 className="au-display-sm">{this.props.label}</h3>
                    <br/>

                    <input
                        className="custom-file-input"
                        type="file"
                        id={buttonUniqueId}
                        name="file"
                        aria-describedby={`${this.props.id}-error`}
                        accept={this.props.acceptedFiles}
                        multiple={allowMultipleUploads}
                        onChange={this.changeEvent} disabled={disableButton}/>
                    <AUlabel
                        tabIndex="0"
                        onKeyPress={(event) => this.addFilesKeyPressEvent(event, buttonUniqueId)}
                        aria-label={this.props.buttonLabel} text={this.props.buttonLabel}
                        className="au-btn au-btn--secondary"
                        disabled={disableButton}
                        htmlFor={buttonUniqueId}/>
                    <br/>
                    <span>{this.props.toolTip}</span>
                </div>
                <div className="uploaded-files" id="uploaded-files">
                    <ul>
                        {this.state.answer.fileDetails.map(item => (
                            <li key={item.file.name}
                                className={!item.isValid && this.props.showError ? "invalid-file" : ""}>
                                <a href={item.blobURL}
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   tabIndex="0"
                                   className="file-name"
                                   aria-label={item.file.name}>
                                    {item.file.name} ({this.getFileSize(item.file)} MB)
                                </a>

                                <span tabIndex="0"
                                      onKeyPress={(event) => this.removeFileKeyPressEvent(event, item.UUID)}
                                      onClick={() => {
                                          this.removeDocument(item.UUID)
                                      }} className="remove-file close" role="button"
                                      aria-label="Press Enter to delete the file {item.name}"> x </span>

                            </li>
                        ))}
                    </ul>
                </div>
                {this.props.createErrorMessage(this.state.errorMessage)}
            </AUformGroup>
        )
    }
}

FilePicker.defaultProps = {
    buttonLabel: "Choose file",
    acceptedFiles: ".pdf,.doc,.docx,.xls,.xlsx,.csv",
    maxFileSize: 2048,
    maxNoOfFilesAllowed: 1,
    enabled: true,
    mandatory: true,
    errorMessages: {
        //TODO get the generic error messages from UX
        maxNoOfFilesReached: "Allowed only X files",
        maxSizeReached: "Upload document more than XX MB",
        invalidFileType: "The file type is not supported",
        invalidFileName: "Uploaded file name contains unsupported characters. Supported characters are alphabets, numbers and special characters , _ -. Please check the file you're trying to upload."
    }
};

FilePicker.propTypes = {
    id: PropTypes.string.isRequired,
    stepid: PropTypes.string.isRequired,
    type: PropTypes.string,
    toolTip: PropTypes.string,
    buttonLabel: PropTypes.string,
    acceptedFiles: PropTypes.string,
    maxNoOfFilesAllowed: PropTypes.number,
    maxFileSize: PropTypes.number,
    createErrorMessage: PropTypes.func,
    getStatus: PropTypes.func,
    mandatory: PropTypes.bool,
    label: PropTypes.string,
    returnQuestionData: PropTypes.func,
    existingAnswer: PropTypes.object,
    errorMessages: PropTypes.object,
    showError: PropTypes.bool
};

export default FilePicker;